import React, { useState, useEffect, useCallback } from 'react';
import { hotelsApi, dashboardApi, shiftsApi } from '../../services/api';
import { fmt } from '../../utils/billing';

export default function MultiHotelDashboard({ onNavigate }) {
    const [activeTab, setActiveTab] = useState('ops'); // 'ops', 'finance', 'staff'
    const [loading, setLoading] = useState(true);
    const [hotels, setHotels] = useState([]);
    const [hotelStats, setHotelStats] = useState({}); // hotelId -> dash results
    const [allShifts, setAllShifts] = useState([]); // Array of all global shifts

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const hList = await hotelsApi.list();
            setHotels(hList);

            // Fetch live financials & shifts for each hotel sequentially or mapped
            const results = await Promise.all(hList.map(async (h) => {
                try {
                    const stats = await dashboardApi.stats(h.id).catch(() => ({}));
                    const shifts = await shiftsApi.list(h.id).catch(() => []);
                    return { hotelId: h.id, stats, shifts: (shifts || []).map(s => ({ ...s, hotelName: h.name })) };
                } catch (e) { return null; }
            }));
            
            const statsMap = {};
            let combinedShifts = [];
            results.filter(Boolean).forEach(r => {
                if (r.stats && Object.keys(r.stats).length) statsMap[r.hotelId] = r.stats;
                if (r.shifts && r.shifts.length) combinedShifts = [...combinedShifts, ...r.shifts];
            });
            
            setHotelStats(statsMap);
            setAllShifts(combinedShifts.sort((a, b) => new Date(b.start_at || 0) - new Date(a.start_at || 0)));
        } catch (err) {
            console.error('Master Dashboard Load Fail:', err);
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading) return (
        <div style={{ minHeight: '80vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
             <div className="spin" style={{ fontSize: 40 }}>🌀</div>
             <div style={{ fontWeight: 800, color: 'var(--muted)', fontSize: 13 }}>GATHERING GLOBAL PERFORMANCE DATA...</div>
        </div>
    );

    // CUMULATIVE FINANCIALS
    const totalCash = Object.values(hotelStats).reduce((acc, s) => acc + (Number(s.todayPay?.cash) || 0), 0);
    const totalUpi = Object.values(hotelStats).reduce((acc, s) => acc + (Number(s.todayPay?.upi) || 0), 0);
    const totalCard = Object.values(hotelStats).reduce((acc, s) => acc + (Number(s.todayPay?.card) || 0), 0);
    const cumulativeRevenue = totalCash + totalUpi + totalCard;
    const networkInventory = Object.values(hotelStats).reduce((acc, s) => acc + (Number(s.roomStats?.total) || 0), 0);
    const networkBookings = Object.values(hotelStats).reduce((acc, s) => acc + (Number(s.roomStats?.occupied) || 0), 0);
    const networkVacant = Object.values(hotelStats).reduce((acc, s) => acc + (Number(s.roomStats?.vacant) || 0), 0);
    const networkExpenses = Object.values(hotelStats).reduce((acc, s) => acc + (Number(s.expenses?.today_exp) || 0), 0);

    // STAFF ANALYTICS
    const userMap = {};
    allShifts.forEach(s => {
        if (!userMap[s.user_id]) {
            userMap[s.user_id] = { id: s.user_id, name: s.user_name, shifts: [], totalRev: 0, totalHours: 0, daysSet: new Set() };
        }
        userMap[s.user_id].shifts.push(s);
        userMap[s.user_id].totalRev += Number(s.total_collected || 0);
        userMap[s.user_id].daysSet.add(new Date(s.start_at).toDateString());
        
        if (s.end_at) {
            const h = (new Date(s.end_at) - new Date(s.start_at)) / 3600000;
            userMap[s.user_id].totalHours += h;
        }
    });

    const staffList = Object.values(userMap).sort((a, b) => b.totalRev - a.totalRev);
    const hotelOverviewCards = hotels.map(h => {
        const stats = hotelStats[h.id] || {};
        return {
            ...h,
            totalInventory: Number(stats.roomStats?.total) || 0,
            bookings: Number(stats.roomStats?.occupied) || 0,
            vacant: Number(stats.roomStats?.vacant) || 0,
            amountCollected: Number(stats.todayPay?.total) || 0,
            expenses: Number(stats.expenses?.today_exp) || 0
        };
    });

    return (
        <div style={{ padding: 24, background: 'var(--bg)', minHeight: '100vh' }}>
            {/* Top Toolbar */}
            <div style={{ marginBottom: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 20 }}>
                <div>
                    <h1 style={{ margin: 0, fontWeight: 900, fontSize: 32, letterSpacing: '-0.5px' }}>🏢 Global Network Oversight</h1>
                    <p style={{ margin: '4px 0 0', opacity: 0.6, fontWeight: 500 }}>Executive Management Console</p>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ background: 'var(--s2)', padding: 4, borderRadius: 14, display: 'flex' }}>
                        <button className={`btn ${activeTab === 'ops' ? 'btn-gold' : 'btn-ghost'}`} style={{ borderRadius: 10, height: 40, border: 'none', padding: '0 20px', fontSize: 13 }} onClick={() => setActiveTab('ops')}>Operations</button>
                        <button className={`btn ${activeTab === 'finance' ? 'btn-gold' : 'btn-ghost'}`} style={{ borderRadius: 10, height: 40, border: 'none', padding: '0 20px', fontSize: 13 }} onClick={() => setActiveTab('finance')}>Financials</button>
                        <button className={`btn ${activeTab === 'staff' ? 'btn-gold' : 'btn-ghost'}`} style={{ borderRadius: 10, height: 40, border: 'none', padding: '0 20px', fontSize: 13 }} onClick={() => setActiveTab('staff')}>HR & Performance</button>
                    </div>
                    <button className="btn btn-gold" onClick={load} style={{ height: 48, borderRadius: 14, fontWeight: 800 }}>🔄 Refresh</button>
                </div>
            </div>

            {activeTab === 'ops' && (
                <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 32 }}>
                        {[
                            { label: 'Inventory', value: networkInventory, tone: '#1f2937' },
                            { label: 'Bookings', value: networkBookings, tone: 'var(--gold)' },
                            { label: 'Vacant', value: networkVacant, tone: 'var(--success)' },
                            { label: 'Collected Till Now', value: fmt(cumulativeRevenue), tone: '#0f766e', isMoney: true },
                            { label: 'Expenses', value: fmt(networkExpenses), tone: 'var(--danger)', isMoney: true }
                        ].map((item) => (
                            <div key={item.label} className="panel" style={{ padding: 20, borderRadius: 22 }}>
                                <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--muted)' }}>{item.label}</div>
                                <div style={{ fontSize: item.isMoney ? 26 : 32, fontWeight: 900, marginTop: 10, color: item.tone }}>{item.value}</div>
                                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>Live network snapshot</div>
                            </div>
                        ))}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 24 }}>
                        {hotelOverviewCards.map(card => (
                            <div
                                key={card.id}
                                style={{
                                    background: 'linear-gradient(180deg, #ffffff 0%, #fbf7ef 100%)',
                                    borderRadius: 28,
                                    border: '1px solid rgba(214, 199, 170, 0.7)',
                                    padding: 24,
                                    boxShadow: '0 20px 45px rgba(31, 41, 55, 0.06)'
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                                            <div style={{ width: 12, height: 12, borderRadius: 999, background: card.color || 'var(--gold)', boxShadow: `0 0 0 6px ${card.color || 'rgba(200,151,42,0.18)'}` }} />
                                            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--muted)' }}>{card.city || 'Property'}</div>
                                        </div>
                                        <h3 style={{ margin: 0, fontWeight: 900, fontSize: 26, letterSpacing: '-0.4px' }}>{card.name}</h3>
                                    </div>
                                    <div style={{ padding: '8px 12px', borderRadius: 999, background: 'rgba(200,151,42,0.12)', color: 'var(--accent)', fontSize: 11, fontWeight: 800 }}>
                                        Hotel Summary
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 20 }}>
                                    {[
                                        { label: 'Total Inventory', value: card.totalInventory, color: '#111827' },
                                        { label: 'Bookings', value: card.bookings, color: 'var(--gold)' },
                                        { label: 'Vacant', value: card.vacant, color: 'var(--success)' },
                                        { label: 'Collected Till Now', value: fmt(card.amountCollected), color: '#0f766e', money: true },
                                        { label: 'Expenses', value: fmt(card.expenses), color: 'var(--danger)', money: true }
                                    ].map((stat) => (
                                        <div key={stat.label} style={{ background: 'rgba(255,255,255,0.84)', border: '1px solid rgba(216, 205, 182, 0.9)', borderRadius: 18, padding: '14px 16px' }}>
                                            <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 8 }}>{stat.label}</div>
                                            <div style={{ fontSize: stat.money ? 20 : 28, fontWeight: 900, color: stat.color, lineHeight: 1.1 }}>{stat.value}</div>
                                        </div>
                                    ))}
                                </div>

                                <button className="btn btn-ghost" style={{ width: '100%', borderRadius: 14, fontWeight: 700 }} onClick={() => onNavigate('dashboard', card)}>
                                    Open detailed dashboard
                                </button>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {activeTab === 'finance' && (
                <>
                    <div style={{ background: 'linear-gradient(135deg, #111, #333)', borderRadius: 32, padding: 48, marginBottom: 40, color: 'white' }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: 2 }}>LIVE NETWORK REVENUE</div>
                        <div style={{ fontSize: 72, fontWeight: 950, margin: '16px 0', letterSpacing: '-3px' }}>{fmt(cumulativeRevenue)}</div>
                        <div style={{ display: 'flex', gap: 48, marginTop: 32 }}>
                            <div><div style={{ fontSize: 10, opacity: 0.6, fontWeight: 800 }}>CASH</div><div style={{ fontSize: 24, fontWeight: 800 }}>{fmt(totalCash)}</div></div>
                            <div><div style={{ fontSize: 10, opacity: 0.6, fontWeight: 800 }}>UPI</div><div style={{ fontSize: 24, fontWeight: 800 }}>{fmt(totalUpi)}</div></div>
                            <div><div style={{ fontSize: 10, opacity: 0.6, fontWeight: 800 }}>CARD</div><div style={{ fontSize: 24, fontWeight: 800 }}>{fmt(totalCard)}</div></div>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 24 }}>
                        {hotels.map(h => {
                            const p = hotelStats[h.id]?.todayPay || { total: 0 };
                            return (
                                <div key={h.id} className="panel" style={{ padding: 28, borderRadius: 24 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                                        <div><h3 style={{ margin: 0 }}>{h.name}</h3><div style={{ fontSize: 11, opacity: 0.5 }}>{h.city}</div></div>
                                        <div style={{ textAlign: 'right' }}><div style={{ fontSize: 24, fontWeight: 900 }}>{fmt(p.total)}</div><div style={{ fontSize: 9, opacity: 0.4 }}>TODAY</div></div>
                                    </div>
                                    <button className="btn btn-ghost" style={{ width: '100%', borderRadius: 12 }} onClick={() => onNavigate('dashboard', h)}>Inspect property dashboard →</button>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {activeTab === 'staff' && (
                <div className="animate-in">
                    {/* Staff Stats Overview */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24, marginBottom: 40 }}>
                         {staffList.slice(0, 3).map((s, idx) => (
                             <div key={s.id} style={{ background: 'white', borderRadius: 24, border: '1px solid var(--border)', padding: 24, position: 'relative', overflow: 'hidden' }}>
                                 <div style={{ position: 'absolute', right: -10, top: -10, fontSize: 60, opacity: 0.1 }}>{idx === 0 ? '🏆' : idx === 1 ? '🥈' : '🥉'}</div>
                                 <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                                     <div style={{ width: 56, height: 56, borderRadius: 16, background: 'var(--s1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 900 }}>{s.name.charAt(0)}</div>
                                     <div>
                                         <div style={{ fontWeight: 800, fontSize: 18 }}>{s.name}</div>
                                         <div style={{ fontSize: 11, color: 'var(--success)', fontWeight: 700 }}>TOP PERFORMER</div>
                                     </div>
                                 </div>
                                 <div style={{ marginTop: 20, display: 'flex', justifyContent: 'space-between' }}>
                                     <div><div style={{ fontSize: 9, opacity: 0.5 }}>ATTENDANCE</div><div style={{ fontWeight: 800 }}>{s.daysSet.size} Days</div></div>
                                     <div style={{ textAlign: 'right' }}>
                                         <div style={{ fontSize: 9, opacity: 0.5 }}>COLS. GENERATED</div>
                                         <div style={{ fontWeight: 900, color: 'var(--gold)' }}>{fmt(s.totalRev)}</div>
                                     </div>
                                 </div>
                             </div>
                         ))}
                    </div>

                    <div className="grid-2">
                        {/* RECENT LOG ENTRIES */}
                        <div className="panel" style={{ borderRadius: 24 }}>
                            <div className="panel-head"><span className="panel-title">🕒 Real-Time Staff Log (Last 50)</span></div>
                            <div style={{ padding: 20, maxHeight: 600, overflowY: 'auto' }}>
                                {allShifts.map(s => {
                                    const dur = s.end_at ? (new Date(s.end_at) - new Date(s.start_at)) / 3600000 : null;
                                    return (
                                        <div key={s.id} style={{ display: 'flex', gap: 16, padding: '16px 0', borderBottom: '1px solid var(--border)' }}>
                                            <div style={{ width: 44, height: 44, borderRadius: 10, background: s.end_at ? 'var(--s1)' : 'rgba(28,209,161,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>{s.end_at ? '🚪' : '⚡'}</div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <span style={{ fontWeight: 800 }}>{s.user_name}</span>
                                                    <span style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase' }}>{s.hotelName}</span>
                                                </div>
                                                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                                                    {s.end_at ? `Completed shift: ${dur.toFixed(1)} hrs` : `Active now: Joined at ${new Date(s.start_at).toLocaleTimeString()}`}
                                                </div>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ fontSize: 12, fontWeight: 700 }}>{new Date(s.start_at).toLocaleDateString()}</div>
                                                <div style={{ fontSize: 10, color: 'var(--gold)', fontWeight: 800 }}>{fmt(s.total_collected || 0)} handled</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* STAFF CAPACITY SUMMARY */}
                        <div>
                            <h3 style={{ margin: '0 0 16px', fontWeight: 800 }}>📉 Workforce Analytics</h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {staffList.map(s => (
                                    <div key={s.id} style={{ background: 'white', borderRadius: 16, border: '1px solid var(--border)', padding: 20 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                                            <div style={{ fontWeight: 800 }}>{s.name}</div>
                                            <div style={{ background: 'var(--gold)', color: 'white', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 800 }}>{s.daysSet.size} DAYS WORKED</div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                            <div style={{ borderLeft: '3px solid var(--border)', paddingLeft: 12 }}>
                                                <div style={{ fontSize: 9, opacity: 0.5 }}>COLS PERFORMANCE</div>
                                                <div style={{ fontWeight: 800 }}>{fmt(s.totalRev)}</div>
                                                <div style={{ fontSize: 10, opacity: 0.6 }}>Avg {fmt(s.totalRev / (s.shifts.length || 1))} / shift</div>
                                            </div>
                                            <div style={{ borderLeft: '3px solid var(--border)', paddingLeft: 12 }}>
                                                <div style={{ fontSize: 9, opacity: 0.5 }}>TIME COMMITMENT</div>
                                                <div style={{ fontWeight: 800 }}>{s.totalHours.toFixed(1)} Hrs</div>
                                                <div style={{ fontSize: 10, opacity: 0.6 }}>Avg { (s.totalHours / (s.shifts.length || 1)).toFixed(1) } hrs/day</div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
